import {
  CpsApprovalStatus,
  CpsPermission,
  CpsClient,
  CpsPod,
  CpsRelationship,
  CpsReportDetailsResponse,
  CpsInternalLogsResponse,
  CpsClientLogsResponse,
  CpsReportInput,
  CpsMonthReportStats,
  CpsUser
} from '@blisspointmedia/bpm-types/dist/CPS';
import { MONTHS } from '@Components/DatePicker/DatePickerButton/DatePickerConstants';

// Re-export bpm-types used by sibling components
// Enums are values and can be re-exported directly
export { CpsApprovalStatus, CpsPermission };
export { MONTHS };
// Interfaces need `export type` under isolatedModules
export type {
  CpsReportDetailsResponse,
  CpsInternalLogsResponse,
  CpsClientLogsResponse,
  CpsReportInput,
  CpsMonthReportStats,
  CpsUser,
  CpsClient,
  CpsPod,
  CpsRelationship
};

// --- Frontend-specific types ---

interface CpsChannel {
  id: string;
  name: string;
}

interface CpsClientSearchRelationship {
  id: string;
  name: string;
  salesforce_id: string;
  is_active: boolean;
  channel: CpsChannel | null;
}

export interface CpsClientSearchResult {
  id: string;
  name: string;
  active: boolean;
  salesforce_id: string | null;
  pod: CpsPod | null;
  relationships: CpsClientSearchRelationship[];
}

interface CpsUserRelationshipClient {
  id: string;
  client: CpsClient;
  relationship: CpsRelationship;
}

export interface CpsCurrentUserResponse extends CpsUser {
  pod: CpsPod | null;
  lastReport: CpsReportDetailsResponse | null;
  userRelationshipClients: CpsUserRelationshipClient[];
}

export interface CpsManagedEmployeeResponse {
  id: string;
  name: string;
  email: string;
  permission: string;
  is_active: boolean;
  pod: CpsPod | null;
  lastReport: CpsReportDetailsResponse | null;
}

export interface CpsClientTimeLogForm {
  client: CpsClientSearchResult | null;
  relationship: { id: string; name: string } | null;
  campaignExecutionHours: number;
  planningStrategyHours: number;
  clientCommsHours: number;
  reportingHours: number;
  insightsAnalysisHours: number;
  externalClientMeetingHours: number;
  internalClientMeetingHours: number;
  otherAdminHours: number;
  totalHours: number;
}

export interface CpsReportForm {
  id?: string;
  approvalStatus?: string;
  totalHoursWorked: number;
  internalTimeLogs: CpsInternalLogsResponse;
  clientTimeLogs: CpsClientTimeLogForm[];
}

// --- UI constants ---

export const CpsTab = {
  SURVEY: 'survey',
  REPORTS: 'reports',
  ADMIN: 'admin'
} as const;
export type CpsTabType = (typeof CpsTab)[keyof typeof CpsTab];

export const ReportSubTab = {
  PENDING: 'pending',
  REJECTED: 'rejected',
  APPROVED: 'approved',
  AWAITING: 'awaiting'
} as const;
export type ReportSubTabType = (typeof ReportSubTab)[keyof typeof ReportSubTab];

export const CLIENT_TIME_FIELDS = [
  'campaignExecutionHours',
  'planningStrategyHours',
  'clientCommsHours',
  'reportingHours',
  'insightsAnalysisHours',
  'externalClientMeetingHours',
  'internalClientMeetingHours',
  'otherAdminHours'
] as const;
export type ClientTimeFieldKey = (typeof CLIENT_TIME_FIELDS)[number];

export const INTERNAL_TIME_FIELDS = [
  'totalSalesSupportHours',
  'totalInternalTaskHours',
  'totalSolutionsSupportHours',
  'totalInternalMeetingHours',
  'totalVendorMeetingHours',
  'totalPeopleManagementHours'
] as const;
export type InternalTimeFieldKey = (typeof INTERNAL_TIME_FIELDS)[number];

export const CLIENT_COLUMN_HEADERS: { label: string; key: ClientTimeFieldKey; tooltip: string }[] =
  [
    {
      label: 'Campaign Execution',
      key: 'campaignExecutionHours',
      tooltip:
        'Managing and implementing campaign activities, including setting up campaigns, adjusting bids, targeting, and optimizing performance.'
    },
    {
      label: 'Planning & Strategy',
      key: 'planningStrategyHours',
      tooltip:
        'Developing comprehensive marketing strategies and plans, involving strategic tasks such as setting marketing objectives, campaign planning, identifying target audiences, and budgeting.'
    },
    {
      label: 'Client Comms',
      key: 'clientCommsHours',
      tooltip:
        'Routine, everyday interactions with clients via emails, Slack messages, and other communications. Communication that supports project flow and client relations without formal presentation or structured agenda.'
    },
    {
      label: 'Reporting',
      key: 'reportingHours',
      tooltip:
        'Gathering and organizing performance data and budget information into structured reports.'
    },
    {
      label: 'Insights & Analysis',
      key: 'insightsAnalysisHours',
      tooltip:
        'Analyzing reporting data to identify trends, competitor benchmarks, and formulate strategic recommendations.'
    },
    {
      label: 'External Meetings',
      key: 'externalClientMeetingHours',
      tooltip:
        'Structured meetings with clients, such as strategy sessions, review meetings, and discussions about campaign direction, with specific agendas and necessary preparation.'
    },
    {
      label: 'Internal Meetings',
      key: 'internalClientMeetingHours',
      tooltip:
        'Internal meetings focused on managing and coordinating client projects, including strategy discussions and project updates, distinct from direct client interactions.'
    },
    {
      label: 'Other / Admin',
      key: 'otherAdminHours',
      tooltip:
        "Encompasses administrative tasks and miscellaneous activities that support client projects but don't fall into the above categories."
    }
  ];

export const INTERNAL_TIME_CONFIG: {
  label: string;
  key: InternalTimeFieldKey;
  tooltip: string;
}[] = [
  {
    label: 'Internal Tasks',
    key: 'totalInternalTaskHours',
    tooltip:
      "This covers internal tasks and general activities of a broader nature that aren't linked to specific clients, as well as all support activities related to the Solutions Champion program. If you're unsure whether the time should be categorized as client work or internal tasks, please check with your manager."
  },
  {
    label: 'New Business / Sales Support',
    key: 'totalSalesSupportHours',
    tooltip:
      'Work related to supporting or auditing new business opportunities or for expanding services or products to existing clients. This includes any activities before the start of a client relationship or the start of the expanded relationship.'
  },
  {
    label: 'Solutions Support',
    key: 'totalSolutionsSupportHours',
    tooltip:
      'For designated Solutions Champions only, this includes all support activities related to the Solutions Champion program, including associated meetings and projects.'
  },
  {
    label: 'Internal Meetings',
    key: 'totalInternalMeetingHours',
    tooltip:
      'Meetings such as huddles, training sessions, learning and development, capability meetings, vendor meetings, and other discussions not focused on any one particular client.'
  },
  {
    label: 'Vendor Meetings',
    key: 'totalVendorMeetingHours',
    tooltip:
      'Discussions with partners such as Google, Meta, TikTok, and others, focused on industry insights, partnership development, and strategic collaborations. While client-specific topics may be discussed, these meetings are not centered on any one particular client.'
  },
  {
    label: 'People Management',
    key: 'totalPeopleManagementHours',
    tooltip:
      'For people managers only, this includes time spent on managerial responsibilities such as mentoring and coaching team members, providing feedback, conducting performance reviews, and addressing team development needs.'
  }
];

export const SURVEY_DESCRIPTION_1 =
  'Submit the average weekly hours worked per client for the last month. Your hours are not limited to a standard 40-hour week and should accurately represent time spent on each client. Exclude any weeks that included time off or company holidays, and instead focus on a typical workweek that reflects your usual workload for the given month. Please provide a breakdown of the time spent on the individual categories listed for each client. Hover over each category to see its description. Detailed instructions can be found';
export const SURVEY_DESCRIPTION_2 =
  'Our goal with category-based submissions is not about performance management; it is to gather actionable insights for improving day-to-day work and time management. By identifying areas for targeted improvements, we can offload or automate tasks, positively impacting workflows.';
export const INSTRUCTIONS_LINK =
  'https://docs.google.com/document/d/1oINA0kts8BMUB-xaVkhcvaH_RguklBUsfpd_99A5UWY/edit';

export const SyncTarget = {
  USERS: 'Users',
  CLIENTS: 'Clients',
  RELATIONSHIPS: 'Relationships',
  CHANNELS: 'Channels',
  PODS: 'Pods'
} as const;
export type SyncTargetType = (typeof SyncTarget)[keyof typeof SyncTarget];

export const SYNC_ENDPOINT_MAP: Record<SyncTargetType, string> = {
  [SyncTarget.USERS]: '/cps/syncUsers',
  [SyncTarget.CLIENTS]: '/cps/syncClients',
  [SyncTarget.RELATIONSHIPS]: '/cps/syncRelationships',
  [SyncTarget.CHANNELS]: '/cps/syncChannels',
  [SyncTarget.PODS]: '/cps/syncPods'
};

export const ALL_SYNC_TARGETS: SyncTargetType[] = Object.values(SyncTarget);

export const BLANK_CLIENT_TIME_LOG: CpsClientTimeLogForm = {
  client: null,
  relationship: null,
  campaignExecutionHours: 0,
  planningStrategyHours: 0,
  clientCommsHours: 0,
  reportingHours: 0,
  insightsAnalysisHours: 0,
  externalClientMeetingHours: 0,
  internalClientMeetingHours: 0,
  otherAdminHours: 0,
  totalHours: 0
};

export const BLANK_INTERNAL_LOGS: CpsInternalLogsResponse = {
  totalSalesSupportHours: 0,
  totalInternalTaskHours: 0,
  totalInternalMeetingHours: 0,
  totalVendorMeetingHours: 0,
  totalSolutionsSupportHours: 0,
  totalPeopleManagementHours: 0
};

export const BLANK_REPORT: CpsReportForm = {
  totalHoursWorked: 0,
  internalTimeLogs: { ...BLANK_INTERNAL_LOGS },
  clientTimeLogs: []
};

// --- Utility functions ---

export const hoursToMinutes = (hours: number): number => Math.round(hours * 60);

export const computeLineItemTotal = (log: CpsClientTimeLogForm): number =>
  CLIENT_TIME_FIELDS.reduce((sum, key) => sum + log[key], 0);

export const computeGrandTotal = (
  clientTimeLogs: CpsClientTimeLogForm[],
  internalTimeLogs: CpsInternalLogsResponse
): number => {
  const clientTotal = clientTimeLogs.reduce((sum, log) => sum + log.totalHours, 0);
  const internalTotal = INTERNAL_TIME_FIELDS.reduce((sum, key) => sum + internalTimeLogs[key], 0);
  return clientTotal + internalTotal;
};

export const transformReportToInput = (
  report: CpsReportForm,
  startDate: string
): CpsReportInput => ({
  startDate,
  totalMinutesWorked: hoursToMinutes(report.totalHoursWorked),
  clientTimeLogs: report.clientTimeLogs.map(log => ({
    clientId: log.client?.id ?? '',
    relationshipId: log.relationship?.id ?? '',
    campaignExecutionMinutes: hoursToMinutes(log.campaignExecutionHours),
    planningStrategyMinutes: hoursToMinutes(log.planningStrategyHours),
    clientCommsMinutes: hoursToMinutes(log.clientCommsHours),
    reportingMinutes: hoursToMinutes(log.reportingHours),
    insightsAnalysisMinutes: hoursToMinutes(log.insightsAnalysisHours),
    externalClientMeetingMinutes: hoursToMinutes(log.externalClientMeetingHours),
    internalClientMeetingMinutes: hoursToMinutes(log.internalClientMeetingHours),
    otherAdminMinutes: hoursToMinutes(log.otherAdminHours),
    totalMinutes: hoursToMinutes(log.totalHours)
  })),
  internalTimeLog: {
    totalMinutesSalesSupport: hoursToMinutes(report.internalTimeLogs.totalSalesSupportHours),
    totalMinutesInternalTasks: hoursToMinutes(report.internalTimeLogs.totalInternalTaskHours),
    totalMinutesSolutionsSupport: hoursToMinutes(
      report.internalTimeLogs.totalSolutionsSupportHours
    ),
    totalMinutesVendorMeetings: hoursToMinutes(report.internalTimeLogs.totalVendorMeetingHours),
    totalMinutesInternalMeetings: hoursToMinutes(report.internalTimeLogs.totalInternalMeetingHours),
    totalMinutesPeopleManagement: hoursToMinutes(report.internalTimeLogs.totalPeopleManagementHours)
  }
});

export const validateReport = (input: CpsReportInput): { errors: string[]; warnings: string[] } => {
  const errors: string[] = [];
  const warnings: string[] = [];

  if (!input.clientTimeLogs.length) {
    warnings.push('There are no client line items');
  }

  const seenPairs = new Set<string>();
  input.clientTimeLogs.forEach((log, index) => {
    if (!log.clientId) {
      errors.push(`Line Item #${index + 1} does not have a client selected`);
    }
    if (!log.relationshipId) {
      errors.push(`Line Item #${index + 1} does not have a relationship selected`);
    }
    if (!log.totalMinutes) {
      errors.push(`Line Item #${index + 1} does not have any time entered`);
    }
    const pairKey = `${log.clientId}:${log.relationshipId}`;
    if (seenPairs.has(pairKey) && log.clientId && log.relationshipId) {
      errors.push('There are two line items for the same relationship');
    }
    seenPairs.add(pairKey);
  });

  const uniqueErrors = [...new Set(errors)];
  errors.length = 0;
  errors.push(...uniqueErrors);

  const totalHours = input.totalMinutesWorked / 60;
  if (totalHours === 0) {
    errors.push('Your total hours cannot be 0');
  }
  if (totalHours > 75 && totalHours < 99) {
    warnings.push('Your total hours is greater than 75');
  }
  if (totalHours >= 99) {
    errors.push('Your total hours cannot be greater than 99');
  }

  return { errors, warnings };
};

export const hydrateReportFromLastReport = (
  lastReport: CpsReportDetailsResponse
): CpsReportForm => ({
  id: lastReport.id,
  approvalStatus: lastReport.approvalStatus,
  totalHoursWorked: lastReport.totalHoursWorked,
  internalTimeLogs: { ...lastReport.internalTimeLogs },
  clientTimeLogs: lastReport.clientTimeLogs.map(log => ({
    client: log.client
      ? {
          id: log.client.id,
          name: log.client.name,
          active: log.client.active,
          salesforce_id: log.client.salesforce_id,
          pod: log.pod,
          relationships: []
        }
      : null,
    relationship: log.relationship
      ? { id: log.relationship.id, name: log.relationship.name }
      : null,
    campaignExecutionHours: log.campaignExecutionHours,
    planningStrategyHours: log.planningStrategyHours,
    clientCommsHours: log.clientCommsHours,
    reportingHours: log.reportingHours,
    insightsAnalysisHours: log.insightsAnalysisHours,
    externalClientMeetingHours: log.externalClientMeetingHours,
    internalClientMeetingHours: log.internalClientMeetingHours,
    otherAdminHours: log.otherAdminHours,
    totalHours: log.totalHours
  }))
});

export const buildInitialReport = (user: CpsCurrentUserResponse): CpsReportForm => ({
  totalHoursWorked: 0,
  internalTimeLogs: { ...BLANK_INTERNAL_LOGS },
  clientTimeLogs: user.userRelationshipClients.map(urc => ({
    ...BLANK_CLIENT_TIME_LOG,
    client: {
      id: urc.client.id,
      name: urc.client.name,
      active: urc.client.active,
      salesforce_id: urc.client.salesforce_id,
      pod: null,
      relationships: []
    },
    relationship: { id: urc.relationship.id, name: urc.relationship.name }
  }))
});

export const removeStaleRelationships = (
  report: CpsReportForm,
  user: CpsCurrentUserResponse
): CpsReportForm => {
  const validPairs = new Set(
    user.userRelationshipClients.map(urc => `${urc.client.id}:${urc.relationship.id}`)
  );

  return {
    ...report,
    clientTimeLogs: report.clientTimeLogs.map(log => {
      if (
        log.client &&
        log.relationship &&
        !validPairs.has(`${log.client.id}:${log.relationship.id}`)
      ) {
        return { ...log, relationship: null };
      }
      return log;
    })
  };
};

export const isReportEditableForPeriod = (
  lastReport: CpsReportDetailsResponse | null,
  surveyDate: string
): boolean => {
  if (!lastReport) return false;
  const isSamePeriod = lastReport.periodStartDate === surveyDate;
  const isEditable =
    lastReport.approvalStatus === String(CpsApprovalStatus.REJECTED) ||
    lastReport.approvalStatus === String(CpsApprovalStatus.DRAFT);
  return isSamePeriod && isEditable;
};

export const isReportAlreadySubmitted = (
  lastReport: CpsReportDetailsResponse | null,
  surveyDate: string
): boolean => {
  if (!lastReport) return false;
  const isSamePeriod = lastReport.periodStartDate === surveyDate;
  const isSubmitted =
    lastReport.approvalStatus === String(CpsApprovalStatus.APPROVED) ||
    lastReport.approvalStatus === String(CpsApprovalStatus.PENDING);
  return isSamePeriod && isSubmitted;
};

export const formatSurveyPeriod = (startDate: string): string => {
  const start = new Date(`${startDate}T00:00:00`);
  const end = new Date(start.getFullYear(), start.getMonth() + 1, 0);
  return `${start.toISOString().split('T')[0]} - ${end.toISOString().split('T')[0]}`;
};

export const getYearOptions = (startYear = 2024): string[] => {
  const currentYear = new Date().getFullYear();
  const years: string[] = [];
  for (let y = currentYear; y >= startYear; y--) {
    years.push(String(y));
  }
  return years;
};

export const getPreviousMonthIndex = (): number => {
  const now = new Date();
  return now.getMonth() === 0 ? 11 : now.getMonth() - 1;
};
